'use client';

import { useState, useEffect } from 'react';
import {
  fetchPayments,
  fetchPaymentDetail,
  transitionPayment,
  createPayment,
  Payment,
  AuditEntry,
  API_BASE
} from '../../lib/api';
import {
  Plus,
  RefreshCw,
  Download,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ArrowRightLeft
} from 'lucide-react';

const ACTORS = [
  { email: 'operator@sur.payments', name: 'Ana Operator', role: 'OPERATOR' },
  { email: 'approver@sur.payments', name: 'Carlos Approver', role: 'APPROVER' },
  { email: 'admin@sur.payments', name: 'Diego Admin', role: 'ADMIN' },
];

const ROLE_ACTIONS: Record<string, string[]> = {
  OPERATOR: ['submit', 'process', 'settle'],
  APPROVER: ['approve', 'reject'],
  ADMIN: ['submit', 'approve', 'reject', 'process', 'settle'],
};

export default function Dashboard() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [activeActor, setActiveActor] = useState(ACTORS[0]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const can = (action: string) => ROLE_ACTIONS[activeActor.role]?.includes(action);

  const [formBeneficiary, setFormBeneficiary] = useState('Mariana Silva');
  const [formAccount, setFormAccount] = useState('BR5511988887777');
  const [formAmount, setFormAmount] = useState('1850.00');
  const [formCorridor, setFormCorridor] = useState('USD-BRL');

  const loadData = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const filters: Record<string, string> = {};
      if (statusFilter !== 'ALL') filters.status = statusFilter;
      const res = await fetchPayments(filters);
      setPayments(res.data || []);
      if (selectedPayment) {
        const detail = await fetchPaymentDetail(selectedPayment.id);
        setSelectedPayment(detail.data);
        setAuditLog(detail.audit || []);
      }
    } catch (err: any) {
      setErrorMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const selectPayment = async (p: Payment) => {
    setSelectedPayment(p);
    try {
      const res = await fetchPaymentDetail(p.id);
      setAuditLog(res.audit || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleTransition = async (action: string) => {
    if (!selectedPayment) return;
    setErrorMsg(null);
    try {
      await transitionPayment(selectedPayment.id, action, activeActor.email);
      await loadData();
    } catch (err: any) {
      setErrorMsg(err.message);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    try {
      await createPayment({
        amount_cents: Math.round(parseFloat(formAmount) * 100),
        currency: 'USD',
        beneficiary_name: formBeneficiary,
        beneficiary_account: formAccount,
        corridor: formCorridor,
        actor_email: activeActor.email,
      });
      setIsNewModalOpen(false);
      await loadData();
    } catch (err: any) {
      setErrorMsg(err.message);
    }
  };

  const isCreator = selectedPayment && selectedPayment.created_by_name === activeActor.name;

  return (
    <div className="min-h-screen overflow-x-clip bg-slate-50 text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-30 flex flex-col gap-3 border-b border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <div className="shrink-0 rounded-lg bg-blue-600 px-2.5 py-1.5 text-sm font-bold text-white">SUR</div>
          <div className="min-w-0">
            <h1 className="text-base font-bold leading-tight text-slate-900 sm:text-lg">LatAm Payments Control Plane</h1>
            <p className="text-xs text-slate-500">Maker-Checker • SPEI / PIX Orchestration</p>
          </div>
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
          <div className="grid w-full grid-cols-3 gap-1 rounded-lg border border-slate-200 bg-slate-100 p-1 sm:flex sm:w-auto">
            {ACTORS.map((act) => (
              <button
                key={act.email}
                onClick={() => setActiveActor(act)}
                className={`min-h-11 rounded-md px-1 py-2 text-center text-xs font-medium sm:px-3 ${
                  activeActor.email === act.email
                    ? 'bg-white shadow text-slate-900'
                    : 'text-slate-600'
                }`}
              >
                <><span className="sm:hidden">{act.name.split(" ")[0]}</span><span className="hidden sm:inline">{act.name}</span></>
              </button>
            ))}
          </div>

          <button
            onClick={() => can('submit') && setIsNewModalOpen(true)}
            disabled={!can('submit')}
            className={`flex min-h-11 w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold sm:w-auto ${can('submit') ? 'bg-blue-600 text-white hover:bg-blue-700' : 'cursor-not-allowed bg-slate-200 text-slate-400'}`}
          >
            <Plus className="w-4 h-4" /> New Instruction
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-4 p-4 sm:p-6">
        {errorMsg && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {errorMsg}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
          <div className="flex flex-wrap gap-2">
            {['ALL', 'DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'PROCESSING', 'SETTLED'].map((st) => (
              <button
                key={st}
                onClick={() => setStatusFilter(st)}
                className={`min-h-11 rounded-lg px-3 py-2 text-xs font-semibold ${
                  statusFilter === st ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
                }`}
              >
                {st.replace('_', ' ')}
              </button>
            ))}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <a
              href={`${API_BASE}/payments/export?format=csv`}
              className="flex items-center gap-1.5 border border-slate-300 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-slate-50"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </a>
            <button onClick={loadData} className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-5">
          {/* Payment List */}
          <div className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white lg:col-span-7">
            <div className="px-5 py-3 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase">
              Live Instruction Queue ({payments.length})
            </div>
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {payments.map((p) => (
                <div
                  key={p.id}
                  onClick={() => selectPayment(p)}
                  className={`p-4 cursor-pointer hover:bg-slate-50 ${
                    selectedPayment?.id === p.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                  }`}
                >
                  <div className="flex justify-between">
                    <span className="font-semibold">{p.beneficiary_name}</span>
                    <span className="font-mono font-bold">
                      ${(parseInt(p.amount_cents) / 100).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-slate-500">
                    <span>{p.corridor} • {p.beneficiary_account}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      p.status === 'SETTLED' ? 'bg-emerald-100 text-emerald-800' :
                      p.status === 'PENDING_APPROVAL' ? 'bg-amber-100 text-amber-800' :
                      p.status === 'APPROVED' ? 'bg-blue-100 text-blue-800' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {p.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Detail Panel */}
          <div className={`min-w-0 rounded-xl border border-slate-200 bg-white p-4 sm:p-5 lg:col-span-5 ${selectedPayment ? "block" : "hidden lg:block"}`}>
            {selectedPayment ? (
              <div className="space-y-5">
                <div>
                  <div className="text-xs text-slate-400 font-mono">{selectedPayment.id}</div>
                  <h2 className="text-2xl font-bold mt-1">
                    ${(parseInt(selectedPayment.amount_cents) / 100).toLocaleString()}
                  </h2>
                  <p className="text-sm text-slate-600">
                    {selectedPayment.beneficiary_name} • {selectedPayment.corridor}
                  </p>
                </div>

                {/* Actions */}
                <div className="bg-slate-50 p-4 rounded-xl border space-y-3">
                  <div className="text-xs font-bold text-slate-500 uppercase">Actions ({activeActor.role})</div>
                  
                  {selectedPayment.status === 'DRAFT' && can('submit') && (
                    <button
                      onClick={() => handleTransition('submit')}
                      className="w-full py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg"
                    >
                      Submit for Approval
                    </button>
                  )}

                  {selectedPayment.status === 'PENDING_APPROVAL' && (can('approve') || can('reject')) && (
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handleTransition('approve')}
                        disabled={!!isCreator}
                        className={`py-2 text-sm font-semibold rounded-lg flex items-center justify-center gap-1 ${
                          isCreator
                            ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                            : 'bg-emerald-600 text-white'
                        }`}
                      >
                        <CheckCircle2 className="w-4 h-4" /> Approve
                      </button>
                      <button
                        onClick={() => handleTransition('reject')}
                        className="py-2 bg-red-600 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-1"
                      >
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                      {isCreator && (
                        <p className="col-span-2 text-xs text-amber-700 bg-amber-50 p-2 rounded">
                          Maker-Checker: You created this payment. Another user must approve.
                        </p>
                      )}
                    </div>
                  )}

                  {selectedPayment.status === 'APPROVED' && can('process') && (
                    <button
                      onClick={() => handleTransition('process')}
                      className="w-full py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg"
                    >
                      Process Payment
                    </button>
                  )}

                  {selectedPayment.status === 'PROCESSING' && can('settle') && (
                    <button
                      onClick={() => handleTransition('settle')}
                      className="w-full py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg"
                    >
                      Mark as Settled
                    </button>
                  )}
                </div>

                {/* Audit Log */}
                <div>
                  <h3 className="text-xs font-bold text-slate-500 uppercase mb-2">Audit Trail</h3>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {auditLog.map((log) => (
                      <div key={log.id} className="text-xs p-2 bg-slate-50 rounded border">
                        <div className="flex justify-between">
                          <span className="font-semibold">{log.action}</span>
                          <span className="text-slate-400">{new Date(log.created_at).toLocaleTimeString()}</span>
                        </div>
                        <div className="text-slate-600">{log.actor_name} ({log.actor_role})</div>
                        {log.note && <div className="italic text-slate-500">"{log.note}"</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
                Select a payment to view details and take actions
              </div>
            )}
          </div>
        </div>
      </main>

      {/* New Payment Modal */}
      {isNewModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="max-h-[90vh] w-full max-w-md space-y-4 overflow-y-auto rounded-xl bg-white p-5">
            <h2 className="font-bold text-lg">New Payment Instruction</h2>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Beneficiary</label>
                <input
                  value={formBeneficiary}
                  onChange={(e) => setFormBeneficiary(e.target.value)}
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Account / PIX / CLABE</label>
                <input
                  value={formAccount}
                  onChange={(e) => setFormAccount(e.target.value)}
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm font-mono"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Amount (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Corridor</label>
                <select
                  value={formCorridor}
                  onChange={(e) => setFormCorridor(e.target.value)}
                  className="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="USD-BRL">USD → BRL (PIX)</option>
                  <option value="USD-MXN">USD → MXN (SPEI)</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsNewModalOpen(false)}
                  className="flex-1 border py-2 rounded-lg text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-semibold"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
